<script setup>
import { ref, computed } from 'vue'
import { useForm } from 'vee-validate'
import { toTypedSchema } from '@vee-validate/yup'
import { object, string } from 'yup'
import BcsModal from '../BcsModal.vue'
import BcsDropdownSelect from '../common/BcsDropdownSelect.vue'
import inviteEnvelopeIcon from '../../../assets/images/invite-envelope.svg'

const emit = defineEmits(['submit', 'close'])

const modalRef = ref(null)
const isSubmitting = ref(false)
const serverEmailError = ref('')

// Permission options matching the design image
const permissionOptions = [
  { value: 'general', label: 'General Access', description: 'Can view and manage assigned employers, assessments, and action items.' },
  { value: 'agencyAdmin', label: 'Admin Access', description: 'Full access including Admin Center, user management, and employer assignments.' }
]

// vee-validate + yup schema
const { defineField, errors, validate, resetForm, isFieldTouched } = useForm({
  initialValues: {
    firstName: '',
    lastName: '',
    title: '',
    email: '',
    accountPermissions: ''
  },
  validationSchema: toTypedSchema(
    object({
      firstName: string()
        .trim()
        .required('First name is required'),
      lastName: string()
        .trim()
        .required('Last name is required'),
      title: string()
        .trim()
        .default(''),
      email: string()
        .trim()
        .required('Email is required')
        .email('Please enter a valid email address'),
      accountPermissions: string()
        .required('Account permission is required')
    })
  )
})

const [firstName, firstNameAttrs] = defineField('firstName')
const [lastName, lastNameAttrs] = defineField('lastName')
const [title, titleAttrs] = defineField('title')
const [email, emailAttrs] = defineField('email')
const [accountPermissions, accountPermissionsAttrs] = defineField('accountPermissions')

const isFormValid = computed(() => {
  return (
    firstName.value?.trim() &&
    lastName.value?.trim() &&
    email.value?.trim() &&
    accountPermissions.value &&
    !errors.value.firstName &&
    !errors.value.lastName &&
    !errors.value.email &&
    !errors.value.accountPermissions
  )
})

function openModal() {
  resetForm()
  isSubmitting.value = false
  serverEmailError.value = ''
  modalRef.value.openModal()
}

function setEmailError(message) {
  isSubmitting.value = false
  serverEmailError.value = message
}

function closeModal() {
  modalRef.value.closeModal()
  emit('close')
}

async function onSubmit() {
  const { valid } = await validate()
  if (!valid || isSubmitting.value) return
  isSubmitting.value = true
  emit('submit', {
    firstName: firstName.value.trim(),
    lastName: lastName.value.trim(),
    title: title.value?.trim() || '',
    email: email.value.trim().toLowerCase(),
    accountPermissions: accountPermissions.value
  })
}

defineExpose({ openModal, closeModal, setEmailError })
</script>

<template>
  <BcsModal
    ref="modalRef"
    modalSize="small"
    :modalType="'confirmation'"
    :hideCloseButton="true"
    :showFooter="false"
    :noDefaultStyles="true"
    @closeButtonClicked="closeModal"
  >
    <div class="text-start px-5 pt-3 pb-2">
      <div class="d-flex justify-content-end">
        <button type="button" class="btn-close" @click="closeModal" aria-label="Close"></button>
      </div>
      <div class="w-100 px-4 py-3">
        <div class="text-center mb-2">
          <img :src="inviteEnvelopeIcon" alt="Invite" width="64" height="64" />
        </div>
        <p class="fw-bold text-center mb-1 header-24">Let us know who'll be joining BCS Pro.</p>
        <p class="text-start text-muted text-14 mb-0">
          Your new user will be sent an invite link. Please fill out the details below.
        </p>
      </div>
      <form @submit.prevent="onSubmit" class="px-4">
        <!-- First Name -->
        <div class="mb-3">
          <label class="form-label fw-semibold text-14">First Name</label>
          <input
            v-model="firstName"
            v-bind="firstNameAttrs"
            type="text"
            class="form-control"
            :class="{ 'is-invalid': isFieldTouched('firstName') && errors.firstName }"
            placeholder="Enter first name"
          />
          <div v-if="isFieldTouched('firstName') && errors.firstName" class="invalid-feedback">
            {{ errors.firstName }}
          </div>
        </div>

        <!-- Last Name -->
        <div class="mb-3">
          <label class="form-label fw-semibold text-14">Last Name</label>
          <input
            v-model="lastName"
            v-bind="lastNameAttrs"
            type="text"
            class="form-control"
            :class="{ 'is-invalid': isFieldTouched('lastName') && errors.lastName }"
            placeholder="Enter last name"
          />
          <div v-if="isFieldTouched('lastName') && errors.lastName" class="invalid-feedback">
            {{ errors.lastName }}
          </div>
        </div>

        <!-- Title -->
        <div class="mb-3">
          <label class="form-label fw-semibold text-14">Title</label>
          <input
            v-model="title"
            v-bind="titleAttrs"
            type="text"
            class="form-control"
            placeholder="e.g. Account Representative"
          />
        </div>

        <!-- Email -->
        <div class="mb-3">
          <label class="form-label fw-semibold text-14">Email</label>
          <input
            v-model="email"
            v-bind="emailAttrs"
            type="email"
            class="form-control"
            :class="{ 'is-invalid': (isFieldTouched('email') && errors.email) || serverEmailError }"
            placeholder="Enter email address"
            @input="serverEmailError = ''"
          />
          <div v-if="serverEmailError" class="invalid-feedback">
            {{ serverEmailError }}
          </div>
          <div v-else-if="isFieldTouched('email') && errors.email" class="invalid-feedback">
            {{ errors.email }}
          </div>
        </div>

        <!-- Account Permissions -->
        <div class="mb-4">
          <label class="form-label fw-semibold text-14">Account Permissions</label>
          <BcsDropdownSelect
            :modelValue="accountPermissions"
            @update:modelValue="accountPermissions = $event"
            :options="permissionOptions"
            placeholder="Select Access Type"
            :isInvalid="isFieldTouched('accountPermissions') && !!errors.accountPermissions"
          />
          <div v-if="isFieldTouched('accountPermissions') && errors.accountPermissions" class="invalid-feedback d-block">
            {{ errors.accountPermissions }}
          </div>
        </div>

        <!-- Submit -->
        <div class="text-center mb-2">
          <button
            type="submit"
            class="btn btn-primary px-4 header-20"
            :disabled="!isFormValid || isSubmitting"
          >
          <med>
            Send Account Invite Link
            <i class="bi bi-send-fill ms-1"></i>
            <span v-if="isSubmitting" class="spinner-border spinner-border-sm ms-2" role="status"></span>
          </med>
          </button>
        </div>

        <!-- Cancel -->
        <div class="text-center mb-2">
          <button type="button" class="btn text-primary text-16" @click="closeModal">
            <med>
              Cancel
            </med>
          </button>
        </div>
      </form>
    </div>
  </BcsModal>
</template>

<style scoped lang="scss">
</style>
